import type { ExportWorkbookSheet } from '../services/exportService';
import type { ReportingAggregateResult, ReportingSubmission } from '../services/dashboardData';
import type { FiscalQuarter, ReportingMonth } from '../lib/reportingMonth';
import { deriveSubmissionMetrics, METRICS_KEY_CHART1_CONSISTENCY_PCT, METRICS_KEY_OVERALL_CONSISTENCY_PCT } from '../lib/submissionMetrics';
import { completenessFromUnknownRow } from '../lib/nursingComplianceScoring';
import { normalizeSubmissionShape } from '../services/submissionShape';
import {
  filterSubmissionsForUi,
  isDepartmentHiddenInUi,
  resolveDepartmentDisplayName,
} from '../lib/departmentUi';
import { formatThaiMonthYYBuddhist } from './dateThai';
import type { ExportPeriod } from './exportPeriod';

const QUESTIONS = Array.from({ length: 9 }, (_value, index) => ({
  label: `ข้อ${index + 1}`,
  scoreKey: `question_${index + 1}_score`,
  totalKey: `question_${index + 1}_total`,
  pctKey: `question_${index + 1}_pct`,
}));

type MonthlyResult = {
  reportingMonth: ReportingMonth;
  result: ReportingAggregateResult;
};

export type NurseNotesExportWorkbookParams = {
  period: ExportPeriod;
  fiscalYear: number;
  fiscalQuarter?: FiscalQuarter;
  months: ReportingMonth[];
  monthlyResults: MonthlyResult[];
  deptIdToName: Map<string, string>;
  categoryLabel: string;
  scopeLabel: string;
};

type MetricSummary = {
  submissionsCount: number;
  completenessPct: number | null;
  diseaseCompletenessPct: number | null;
  consistencyPct: number | null;
  questionPcts: Array<number | null>;
};

type DepartmentGroup = {
  departmentId: string;
  departmentName: string;
  submissions: ReportingSubmission[];
};

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return null;
}

function roundPercent(value: number): number {
  return Math.round(value * 100) / 100;
}

function percentage(score: number, total: number): number | null {
  if (!Number.isFinite(score) || !Number.isFinite(total) || total <= 0) return null;
  return roundPercent((score / total) * 100);
}

function getSubmissionMetrics(submission: ReportingSubmission): Record<string, number> {
  const metrics = submission.metrics ?? {};
  if (Object.keys(metrics).length > 0) return metrics;
  return deriveSubmissionMetrics(submission);
}

function shouldExcludeChartTitleForOwnSubmission(title: string): boolean {
  if (title.includes('ตรวจไขว้')) return true;
  if (title.includes('แก้ไข')) return true;
  const lowered = title.toLowerCase();
  return lowered.includes('edit') || lowered.includes('audit') || lowered.includes('cross audit');
}

function includedChartIndexes(submission: ReportingSubmission): number[] {
  const raw = submission as Record<string, unknown>;
  const shape = normalizeSubmissionShape(raw);
  const answers = shape.answers.map((row) => (Array.isArray(row) ? row : []));
  const chartTitles = shape.chartTitles.map((title) => (typeof title === 'string' ? title : String(title ?? '')));
  const isCrossAudit = !!(submission.isCrossAudit || raw.crossAudit === true);
  if (!answers.length) return [];
  if (!isCrossAudit && chartTitles.length) {
    return answers.flatMap((_, index) => {
      const title = chartTitles[index] ?? '';
      return title && !shouldExcludeChartTitleForOwnSubmission(title) ? [index] : [];
    });
  }
  return answers.map((_, index) => index);
}

function pooledDiseaseCompletenessPct(submissions: ReportingSubmission[]): number | null {
  const values: number[] = [];

  submissions.forEach((submission) => {
    const metrics = getSubmissionMetrics(submission);
    const storedPct = toFiniteNumber(metrics.disease_pct);
    if (storedPct !== null) {
      values.push(storedPct);
      return;
    }

    const shape = normalizeSubmissionShape(submission as Record<string, unknown>);
    const answers = shape.answers.map((row) => (Array.isArray(row) ? row : []));
    const chartTitles = shape.chartTitles.map((title) => (typeof title === 'string' ? title : String(title ?? '')));
    let score = 0;
    let full = 0;
    includedChartIndexes(submission).forEach((rowIndex) => {
      const title = chartTitles[rowIndex] ?? '';
      if (!title.includes('รายโรค')) return;
      const result = completenessFromUnknownRow(answers[rowIndex]);
      score += result.score;
      full += result.full;
    });
    if (full > 0) {
      const pct = percentage(score, full);
      if (pct !== null) values.push(pct);
    }
  });

  if (values.length > 0) {
    return roundPercent(values.reduce((sum, value) => sum + value, 0) / values.length);
  }

  let fallbackTotal = 0;
  let fallbackCharts = 0;
  submissions.forEach((submission) => {
    const metrics = getSubmissionMetrics(submission);
    const avgScore = toFiniteNumber(metrics.disease_monthly_avg_score);
    const charts = toFiniteNumber(metrics.disease_charts);
    if (avgScore === null || charts === null || charts <= 0) return;
    fallbackTotal += avgScore * charts;
    fallbackCharts += charts;
  });

  return fallbackCharts > 0 ? percentage(fallbackTotal, fallbackCharts * 9) : null;
}

function summarizeSubmissions(submissions: ReportingSubmission[]): MetricSummary {
  const totals: Record<string, number> = {};
  const consistencyValues: number[] = [];

  submissions.forEach((submission) => {
    const metrics = getSubmissionMetrics(submission);
    Object.entries(metrics).forEach(([key, value]) => {
      const numberValue = toFiniteNumber(value);
      if (numberValue === null) return;
      totals[key] = (totals[key] ?? 0) + numberValue;
    });

    const consistency =
      toFiniteNumber(metrics[METRICS_KEY_OVERALL_CONSISTENCY_PCT]) ??
      toFiniteNumber(metrics[METRICS_KEY_CHART1_CONSISTENCY_PCT]);
    if (consistency !== null) consistencyValues.push(consistency);
  });

  return {
    submissionsCount: submissions.length,
    completenessPct: percentage(totals.monthly_score ?? 0, totals.monthly_total ?? 0),
    diseaseCompletenessPct: pooledDiseaseCompletenessPct(submissions),
    consistencyPct: consistencyValues.length
      ? roundPercent(consistencyValues.reduce((sum, value) => sum + value, 0) / consistencyValues.length)
      : null,
    questionPcts: QUESTIONS.map((question) => {
      const directPct = toFiniteNumber(totals[question.pctKey]);
      const count = submissions.length || 1;
      return percentage(totals[question.scoreKey] ?? 0, totals[question.totalKey] ?? 0) ?? (directPct !== null ? roundPercent(directPct / count) : null);
    }),
  };
}

function departmentDisplayName(deptIdToName: Map<string, string>, departmentId: string): string {
  return resolveDepartmentDisplayName(departmentId, undefined, deptIdToName);
}

function groupByDepartment(
  submissions: ReportingSubmission[],
  deptIdToName: Map<string, string>,
): DepartmentGroup[] {
  const byDepartment = new Map<string, ReportingSubmission[]>();
  filterSubmissionsForUi(submissions).forEach((submission) => {
    const departmentId = typeof submission.departmentId === 'string' && submission.departmentId.trim()
      ? submission.departmentId.trim()
      : 'unknown';
    if (isDepartmentHiddenInUi(departmentId)) return;
    const group = byDepartment.get(departmentId) ?? [];
    group.push(submission);
    byDepartment.set(departmentId, group);
  });
  return Array.from(byDepartment.entries())
    .map(([departmentId, group]) => ({
      departmentId,
      departmentName: departmentDisplayName(deptIdToName, departmentId),
      submissions: group,
    }))
    .sort((a, b) => a.departmentName.localeCompare(b.departmentName, 'th'));
}

function metricCells(summary: MetricSummary): Array<number | null> {
  return [
    summary.submissionsCount,
    summary.completenessPct,
    summary.diseaseCompletenessPct,
    summary.consistencyPct,
    ...summary.questionPcts,
  ];
}

function periodLabel(params: Pick<NurseNotesExportWorkbookParams, 'period' | 'fiscalYear' | 'fiscalQuarter'>): string {
  if (params.period === 'monthly') return 'รายเดือน';
  if (params.period === 'trimester') return params.fiscalQuarter ? `ไตรมาส ${params.fiscalQuarter}/${params.fiscalYear}` : 'รายไตรมาส';
  return `ปีงบประมาณ ${params.fiscalYear}`;
}

function periodTypeLabel(period: ExportPeriod): string {
  if (period === 'monthly') return 'รายเดือน';
  if (period === 'trimester') return 'รายไตรมาส';
  return 'รายปี';
}

function headers(prefix: string[]): string[] {
  return [
    ...prefix,
    'จำนวนรายการ',
    'ร้อยละความสมบูรณ์',
    'ร้อยละความสมบูรณ์รายโรค',
    'ร้อยละความสอดคล้อง',
    ...QUESTIONS.map((question) => `ร้อยละคะแนน${question.label}`),
  ];
}

export function buildNurseNotesExportWorkbook(params: NurseNotesExportWorkbookParams): ExportWorkbookSheet[] {
  const monthResultMap = new Map(params.monthlyResults.map((entry) => [entry.reportingMonth, entry.result]));
  const allSubmissions = filterSubmissionsForUi(
    params.months.flatMap((month) => monthResultMap.get(month)?.submissions ?? []),
  );
  const label = periodLabel(params);
  const overviewSummary = summarizeSubmissions(allSubmissions);

  const overviewRows = [
    headers(['ช่วงเวลา']),
    [label, ...metricCells(overviewSummary)],
  ];

  const departmentRows = [
    headers(['หน่วยงาน', 'ช่วงเวลา']),
    ...groupByDepartment(allSubmissions, params.deptIdToName).map(({ departmentName, submissions }) => [
      departmentName,
      label,
      ...metricCells(summarizeSubmissions(submissions)),
    ]),
  ];

  const monthlyDepartmentRows = [
    headers(['เดือน', 'หน่วยงาน']),
    ...params.months.flatMap((month) => {
      const monthSubmissions = monthResultMap.get(month)?.submissions ?? [];
      return groupByDepartment(monthSubmissions, params.deptIdToName).map(({ departmentName, submissions }) => [
        formatThaiMonthYYBuddhist(month),
        departmentName,
        ...metricCells(summarizeSubmissions(submissions)),
      ]);
    }),
  ];

  return [
    {
      sheetName: 'คำอธิบาย',
      rows: [
        ['หมวดหมู่', params.categoryLabel],
        ['ขอบเขตหน่วยงาน', params.scopeLabel],
        ['ช่วงเวลา', periodTypeLabel(params.period)],
        ['ปีงบประมาณ', params.fiscalYear],
        ...(params.fiscalQuarter ? [['ไตรมาส', params.fiscalQuarter]] : []),
        ['เดือนที่ใช้คำนวณ', params.months.map(formatThaiMonthYYBuddhist).join(', ')],
        ['หมายเหตุ', 'ค่าว่างหมายถึงไม่มีข้อมูลสำหรับตัวชี้วัดนั้นในช่วงเวลาที่เลือก'],
      ],
      columnWidths: [22, 80],
    },
    {
      sheetName: 'สรุปรวม',
      rows: overviewRows,
      freezeHeaderRows: 1,
      autoFilter: true,
    },
    {
      sheetName: 'สรุปตามหน่วยงาน',
      rows: departmentRows,
      freezeHeaderRows: 1,
      autoFilter: true,
    },
    {
      sheetName: 'รายเดือนตามหน่วยงาน',
      rows: monthlyDepartmentRows,
      freezeHeaderRows: 1,
      autoFilter: true,
    },
  ];
}
